/**
 * Google Maps Script Loader
 * Dynamically loads Google Maps with API key from environment variables
 * This prevents exposing the API key in HTML source code
 *
 * Usage:
 *   import { loadGoogleMapsScript } from './utils/loadGoogleMaps';
 *   loadGoogleMapsScript().catch(err => console.error('Maps failed:', err));
 */

/**
 * Load Google Maps JavaScript API dynamically
 * @param {Object} options - Configuration options
 * @param {string} options.libraries - Comma-separated list of libraries (default: 'places')
 * @param {string} options.callback - Callback function name (default: 'googleMapsCallback')
 * @returns {Promise} Resolves when Maps script is loaded
 */
export const loadGoogleMapsScript = (options = {}) => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    // Get API key from environment variable
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.error(
        "Missing REACT_APP_GOOGLE_MAPS_API_KEY environment variable. " +
          "Please add it to your .env.local file. " +
          "Get your key from: https://console.cloud.google.com/google/maps-apis/credentials"
      );
      reject(
        new Error(
          "Google Maps API key not configured. Check console for details."
        )
      );
      return;
    }

    // Merge options with defaults
    const libraries = options.libraries || "places";
    const callback = options.callback || "googleMapsCallback";

    // Create callback function on window
    window[callback] = () => {
      resolve();
    };

    // Create script element
    const script = document.createElement("script");
    script.src =
      `https://maps.googleapis.com/maps/api/js?` +
      `key=${apiKey}&` +
      `libraries=${libraries}&` +
      `loading=async&` +
      `callback=${callback}`;
    script.async = true;
    script.defer = true;

    // Handle errors
    script.onerror = () => {
      reject(
        new Error(
          "Failed to load Google Maps script. " +
            "Check your API key and network connection."
        )
      );
    };

    // Append to document head
    document.head.appendChild(script);
  });
};

/**
 * Check if Google Maps is loaded
 * @returns {boolean} True if Maps API is available
 */
export const isGoogleMapsLoaded = () => {
  return !!(window.google && window.google.maps);
};
